import React, { useMemo, useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  GitBranch,
  Users,
  Clock,
  Settings,
  UserPlus,
  BookOpen,
  FileText,
  Heart,
  Flame,
  Menu,
  Sparkles,
  User,
  UserCog,
  ClipboardCheck,
  Bell,
  ChevronLeft,
  ChevronRight,
  Compass,
  Share2,
  Eye,
  Lock,
  HelpCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { useAuth } from '../../contexts/AuthContext';
import { ArchivePreviewModal } from './ArchivePreviewModal';
import { getArchiveNavActives } from './archiveNavActive';
import { markGuideExportPreviewed } from '../../utils/guideProgress';
import { getMyArchivePersonId } from '../../utils/myArchivePerson';
import { ArchiveSubjectIdentity, archiveSubjectDisplayName } from './ArchiveSubjectIdentity';
import { ArchiveHeldHereLinks } from './ArchiveHeldHereLinks';
import { personHref } from '../../utils/prettyArchiveLinks';
import { memorialsHref } from '../../utils/memorial';
import { useArchiveSidebarCollapse } from '../../hooks/useArchiveSidebarCollapse';
import { GuideLauncher } from '../guide/GuideLauncher';
import { CORE_JOURNEY_UI } from '../../config/testerRelease';

const RAIL_WIDTH = 'w-[5.25rem]';
const RAIL_ICON = 'h-[18px] w-[18px] shrink-0';
const RAIL_HIT = 'mx-auto flex h-11 w-11 items-center justify-center rounded-xl';

function navClass(isActive, collapsed) {
  if (collapsed) {
    return cn(
      RAIL_HIT,
      'relative text-sm transition-all duration-150',
      isActive
        ? 'bg-[hsl(152_28%_22%_/_0.11)] text-[hsl(152_28%_24%)] shadow-[inset_0_0_0_1px_hsl(152_28%_22%_/_0.14)] before:absolute before:left-0 before:top-1/2 before:h-5 before:w-[3px] before:-translate-y-1/2 before:rounded-r-full before:bg-[#c9a961] before:content-[""]'
        : 'text-[hsl(32_10%_42%)] hover:bg-[hsl(42_26%_97%)] hover:text-[hsl(24_12%_22%)]',
    );
  }

  return cn(
    'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors',
    isActive
      ? 'bg-[hsl(38_22%_88%)]/90 font-medium text-[hsl(24_14%_16%)] shadow-sm'
      : 'text-[hsl(32_10%_38%)] hover:bg-[hsl(42_26%_97%)] hover:text-[hsl(24_12%_22%)]',
  );
}

function navIconClass(isActive, collapsed) {
  return cn(
    collapsed ? RAIL_ICON : 'h-4 w-4 shrink-0 opacity-80',
    collapsed && isActive && 'text-[hsl(152_28%_24%)]',
    collapsed && !isActive && 'opacity-90',
  );
}

const mobileBarBtn =
  'flex h-auto flex-1 flex-col gap-0.5 rounded-xl py-2 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground';

function SectionLabel({ children, spaced = false }) {
  return (
    <p
      className={cn(
        'mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-[hsl(32_9%_48%)]',
        spaced && 'mt-5',
      )}
    >
      {children}
    </p>
  );
}

function SidebarRailDivider({ collapsed }) {
  if (!collapsed) return null;
  return (
    <div className="my-1 flex w-full justify-center px-1" aria-hidden="true">
      <div className="h-px w-7 bg-[hsl(38_14%_86%)]/90" />
    </div>
  );
}

function SidebarNavLink({
  to,
  icon: Icon,
  label,
  isActive,
  collapsed,
  onClick,
  testId,
  end,
}) {
  const link = (
    <NavLink
      to={to}
      end={end}
      className={() => navClass(isActive, collapsed)}
      onClick={onClick}
      data-testid={testId}
      aria-label={collapsed ? label : undefined}
    >
      <Icon className={navIconClass(isActive, collapsed)} strokeWidth={1.75} aria-hidden />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={10} className="text-xs font-medium">
          {label}
        </TooltipContent>
      </Tooltip>
    );
  }
  return link;
}

function SidebarRailAction({ label, onClick, icon: Icon, testId }) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className={cn(RAIL_HIT, 'text-[hsl(32_10%_42%)] hover:bg-[hsl(42_26%_97%)] hover:text-[hsl(24_12%_22%)]')}
          onClick={onClick}
          aria-label={label}
          data-testid={testId}
        >
          <Icon className={RAIL_ICON} strokeWidth={1.75} />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={10} className="text-xs font-medium">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Persistent archive navigation: one connected “family archive” mental model.
 * Desktop: collapsible left rail. Mobile: bottom bar + sheet menu.
 */
export function ArchiveContextNav({
  treeId,
  treeLinkBase,
  treeName,
  variant = 'tree',
  personId,
  personLabel,
  personSummary = null,
  personHeldCounts = null,
  onPersonViewNavigate = null,
  canExport = false,
  onExportPdf,
  onExportJson,
  exportingPdf = false,
  exportingJson = false,
  peopleCount = 0,
}) {
  const { user } = useAuth();
  const location = useLocation();
  const { collapsed, toggle } = useArchiveSidebarCollapse();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [localArchivePersonId, setLocalArchivePersonId] = useState(() => getMyArchivePersonId());

  const linkedArchivePersonId = user?.self_person_id || localArchivePersonId;

  useEffect(() => {
    const sync = () => setLocalArchivePersonId(getMyArchivePersonId());
    window.addEventListener('storage', sync);
    window.addEventListener('gb-my-archive-person-updated', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('gb-my-archive-person-updated', sync);
    };
  }, []);

  const actives = useMemo(
    () => getArchiveNavActives(location, { treeId, personId, variant, treeLinkBase: treeLinkBase || undefined }),
    [location, treeId, personId, variant, treeLinkBase],
  );

  const onLinkedArchiveStories = useMemo(() => {
    if (!linkedArchivePersonId) return false;
    const sp = new URLSearchParams(location.search || '');
    const base = personHref({ id: linkedArchivePersonId });
    return (
      location.pathname === base &&
      (sp.get('view') === 'stories' || sp.get('view') === null || sp.get('view') === '')
    );
  }, [location.pathname, location.search, linkedArchivePersonId]);

  const closeSheet = () => setSheetOpen(false);
  const treeBase = treeLinkBase || `/tree/${treeId}`;

  const renderLinks = (linkCollapsed) => (
    <nav
      className={cn('flex flex-col', linkCollapsed ? 'items-center gap-2' : 'gap-0.5')}
      aria-label="Family archive"
    >
      {!linkCollapsed && <SectionLabel>Archive</SectionLabel>}

      {CORE_JOURNEY_UI ? (
        <>
          <SidebarNavLink to="/dashboard" icon={LayoutDashboard} label="Dashboard" isActive={actives.dashboard} collapsed={linkCollapsed} onClick={closeSheet} />
          <SidebarNavLink to={`${treeBase}?tab=grid`} icon={Users} label="People" isActive={actives.treePeople} collapsed={linkCollapsed} onClick={closeSheet} />
          {linkedArchivePersonId ? (
            <SidebarNavLink
              to={`${personHref({ id: linkedArchivePersonId })}?view=stories`}
              icon={BookOpen}
              label="Memories"
              isActive={onLinkedArchiveStories}
              collapsed={linkCollapsed}
              onClick={closeSheet}
              testId="nav-core-memories"
            />
          ) : variant === 'person' && personId ? (
            <SidebarNavLink
              to={`${personHref({ id: personId })}?view=stories`}
              icon={BookOpen}
              label="Memories"
              isActive={actives.personStories}
              collapsed={linkCollapsed}
              onClick={closeSheet}
              testId="nav-core-memories"
            />
          ) : null}
          <SidebarNavLink
            to={`${treeBase}?tab=readiness`}
            icon={ClipboardCheck}
            label="Legacy Book"
            isActive={actives.treeReadiness}
            collapsed={linkCollapsed}
            onClick={closeSheet}
            testId="nav-legacy-book"
          />
          <SidebarNavLink to={`${treeBase}?tab=settings`} icon={Settings} label="Archive settings" isActive={actives.treeSettings} collapsed={linkCollapsed} onClick={closeSheet} />

          {!linkCollapsed && (
            <div className="mt-2 px-1">
              <button
                type="button"
                onClick={() => setAdvancedOpen((v) => !v)}
                className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-medium text-[hsl(32_10%_42%)] hover:bg-[hsl(42_26%_97%)]"
                data-testid="nav-advanced-toggle"
              >
                <span>More features</span>
                <ChevronRight className={cn('h-3.5 w-3.5 transition', advancedOpen && 'rotate-90')} aria-hidden />
              </button>
            </div>
          )}

          {advancedOpen && !linkCollapsed && (
            <div className="flex flex-col gap-0.5">
              <SidebarNavLink to={`${treeBase}?tab=chart`} icon={GitBranch} label="Family tree" isActive={actives.treeChart} collapsed={false} onClick={closeSheet} />
              <SidebarNavLink to={`${treeBase}?tab=timeline`} icon={Clock} label="Timeline" isActive={actives.treeTimeline} collapsed={false} onClick={closeSheet} />
              <SidebarNavLink to="/reminders" icon={Bell} label="Reminders" isActive={location.pathname === '/reminders'} collapsed={false} onClick={closeSheet} />
              <SidebarNavLink to="/vault" icon={Lock} label="Handover Vault" isActive={location.pathname.startsWith('/vault')} collapsed={false} onClick={closeSheet} />
              <SidebarNavLink to={memorialsHref(treeId)} icon={Flame} label="Memorials" isActive={actives.memorials} collapsed={false} onClick={closeSheet} />
              <SidebarNavLink to="/story-studio" icon={Sparkles} label="AI Story Studio" isActive={location.pathname.startsWith('/story-studio')} collapsed={false} onClick={closeSheet} />
              <SidebarNavLink to="/storybook" icon={BookOpen} label="Storybook Mode" isActive={location.pathname.startsWith('/storybook')} collapsed={false} onClick={closeSheet} />
              <SidebarNavLink to={`${treeBase}/members`} icon={UserPlus} label="Invite family" isActive={actives.treeMembers} collapsed={false} onClick={closeSheet} />
            </div>
          )}

          {linkCollapsed && (
            <SidebarRailAction
              label="More features"
              icon={Menu}
              testId="nav-advanced-collapsed"
              onClick={() => {
                setAdvancedOpen(true);
                setSheetOpen(true);
              }}
            />
          )}
        </>
      ) : (
        <>
          <SidebarNavLink to="/dashboard" icon={LayoutDashboard} label="Dashboard" isActive={actives.dashboard} collapsed={linkCollapsed} onClick={closeSheet} />
          <SidebarNavLink to={`${treeBase}?tab=chart`} icon={GitBranch} label="Family tree" isActive={actives.treeChart} collapsed={linkCollapsed} onClick={closeSheet} />
          <SidebarNavLink to={`${treeBase}?tab=grid`} icon={Users} label="People" isActive={actives.treePeople} collapsed={linkCollapsed} onClick={closeSheet} />
          <SidebarNavLink to={`${treeBase}?tab=timeline`} icon={Clock} label="Timeline" isActive={actives.treeTimeline} collapsed={linkCollapsed} onClick={closeSheet} />
          <SidebarNavLink to="/reminders" icon={Bell} label="Reminders" isActive={location.pathname === '/reminders'} collapsed={linkCollapsed} onClick={closeSheet} />
          <SidebarNavLink to="/vault" icon={Lock} label="Family Handover Vault" isActive={location.pathname.startsWith('/vault')} collapsed={linkCollapsed} onClick={closeSheet} testId="nav-handover-vault" />
          <SidebarNavLink to={`${treeBase}/members`} icon={UserPlus} label="Invite family" isActive={actives.treeMembers} collapsed={linkCollapsed} onClick={closeSheet} />
          <SidebarNavLink to={`${treeBase}?tab=settings`} icon={Settings} label="Archive settings" isActive={actives.treeSettings} collapsed={linkCollapsed} onClick={closeSheet} />
        </>
      )}

      {variant === 'person' && personId && (
        <>
          <SidebarRailDivider collapsed={linkCollapsed} />
          {!linkCollapsed && <SectionLabel spaced>This person</SectionLabel>}
          <SidebarNavLink
            to={`${personHref({ id: personId })}?view=stories`}
            icon={BookOpen}
            label={CORE_JOURNEY_UI ? 'Memories' : 'Stories & voice'}
            isActive={actives.personStories}
            collapsed={linkCollapsed}
            onClick={closeSheet}
          />
          <SidebarNavLink to={`${personHref({ id: personId })}?view=documents`} icon={FileText} label="Documents" isActive={actives.personDocuments} collapsed={linkCollapsed} onClick={closeSheet} />
          {!CORE_JOURNEY_UI && (
            <>
              <SidebarNavLink to={`${personHref({ id: personId })}?view=shared`} icon={Share2} label="Shared archive" isActive={actives.personShared} collapsed={linkCollapsed} onClick={closeSheet} />
              <SidebarNavLink to={`${personHref({ id: personId })}?view=family`} icon={Heart} label="Family" isActive={actives.personFamily} collapsed={linkCollapsed} onClick={closeSheet} />
            </>
          )}
        </>
      )}

      <SidebarRailDivider collapsed={linkCollapsed} />
      {!linkCollapsed && (
        <SectionLabel spaced>
          You
          {user?.name && (
            <span className="ml-1 font-normal normal-case tracking-normal text-muted-foreground/70">
              — {user.name}
            </span>
          )}
        </SectionLabel>
      )}

      {linkedArchivePersonId ? (
        linkCollapsed ? (
          <SidebarNavLink
            to={`${personHref({ id: linkedArchivePersonId })}?view=stories`}
            icon={User}
            label="My archive profile"
            isActive={onLinkedArchiveStories}
            collapsed={linkCollapsed}
            onClick={closeSheet}
            testId="nav-my-archive-profile"
          />
        ) : (
          <NavLink
            to={`${personHref({ id: linkedArchivePersonId })}?view=stories`}
            className={() => navClass(onLinkedArchiveStories, linkCollapsed)}
            onClick={closeSheet}
            data-testid="nav-my-archive-profile"
          >
            <User className={navIconClass(onLinkedArchiveStories, linkCollapsed)} strokeWidth={1.75} aria-hidden />
            <span className="flex-1 truncate">My archive profile</span>
            <span className="ml-auto shrink-0 rounded-full bg-primary/10 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide text-primary">
              You
            </span>
          </NavLink>
        )
      ) : (
        <SidebarNavLink to="/dashboard" icon={User} label="Add yourself as a person" isActive={false} collapsed={linkCollapsed} onClick={closeSheet} testId="nav-find-me" />
      )}
      <SidebarNavLink to="/settings" icon={UserCog} label="Account settings" isActive={actives.settings} collapsed={linkCollapsed} onClick={closeSheet} />

      <SidebarRailDivider collapsed={linkCollapsed} />
      {!linkCollapsed && (
        <SectionLabel spaced>{CORE_JOURNEY_UI ? 'Help' : 'Preview & export'}</SectionLabel>
      )}

      {!CORE_JOURNEY_UI && (
        <>
          <SidebarNavLink to={memorialsHref(treeId)} icon={Flame} label="Memorials" isActive={actives.memorials} collapsed={linkCollapsed} onClick={closeSheet} testId="nav-memorials" />
          <SidebarNavLink to="/story-studio" icon={Sparkles} label="AI Story Studio" isActive={location.pathname.startsWith('/story-studio')} collapsed={linkCollapsed} onClick={closeSheet} testId="nav-story-studio" />
          <SidebarNavLink to="/storybook" icon={BookOpen} label="Storybook Mode" isActive={location.pathname.startsWith('/storybook')} collapsed={linkCollapsed} onClick={closeSheet} testId="nav-storybook" />
          <SidebarNavLink to={`${treeBase}?tab=readiness`} icon={ClipboardCheck} label="Keepsake readiness" isActive={actives.treeReadiness} collapsed={linkCollapsed} onClick={closeSheet} testId="nav-keepsake-readiness" />
        </>
      )}
      <SidebarNavLink to="/guide" icon={Compass} label="Guide" isActive={location.pathname === '/guide'} collapsed={linkCollapsed} onClick={closeSheet} testId="nav-guide" />
      <SidebarNavLink to="/help" icon={HelpCircle} label="Help & Guides" isActive={location.pathname === '/help'} collapsed={linkCollapsed} onClick={closeSheet} testId="nav-help-guides" />

      {!CORE_JOURNEY_UI && (
      <div className={cn(linkCollapsed ? 'flex justify-center' : 'mt-2')}>
        {linkCollapsed ? (
          <SidebarRailAction
            label="Preview & export"
            icon={Eye}
            testId="nav-preview-export-collapsed"
            onClick={() => setPreviewOpen(true)}
          />
        ) : (
          <Button
            type="button"
            variant="outline"
            className="w-full justify-center rounded-full border-[hsl(38_14%_82%)] bg-[hsl(42_28%_100%)] text-[hsl(24_12%_22%)] hover:bg-[hsl(42_24%_97%)]"
            onClick={() => {
              setPreviewOpen(true);
              closeSheet();
            }}
            data-testid="nav-preview-export"
          >
            <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.75} />
            Preview &amp; export
          </Button>
        )}
      </div>
      )}
    </nav>
  );

  const collapseToggle = (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            collapsed
              ? cn(RAIL_HIT, 'border border-[hsl(38_14%_88%)] bg-[hsl(42_28%_100%)] text-[hsl(32_10%_42%)] shadow-sm hover:bg-[hsl(42_26%_97%)] hover:text-[hsl(24_12%_22%)]')
              : 'h-8 w-8 shrink-0 rounded-full text-muted-foreground hover:bg-[hsl(42_26%_97%)]',
          )}
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          data-testid="archive-sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className={RAIL_ICON} strokeWidth={1.75} />
          ) : (
            <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={10} className="text-xs font-medium">
        {collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      </TooltipContent>
    </Tooltip>
  );

  return (
    <TooltipProvider delayDuration={300}>
      <aside
        className={cn(
          'hidden shrink-0 flex-col border-r border-[hsl(38_14%_86%)] bg-[hsl(42_28%_98%)]/98 lg:flex',
          'shadow-[1px_0_12px_-8px_hsl(28_14%_18%_/_0.12)]',
          'transition-[width,padding] duration-200 ease-in-out',
          collapsed ? cn(RAIL_WIDTH, 'px-2 py-3') : 'w-[min(17rem,22vw)] pl-4 pr-3 py-6',
        )}
        data-testid="archive-context-nav-desktop"
        data-sidebar-collapsed={collapsed ? 'true' : 'false'}
      >
        <div
          className={cn(
            'shrink-0',
            collapsed ? 'mb-3 flex flex-col items-center gap-2' : 'mb-4 flex items-center justify-between gap-1 px-1',
          )}
        >
          {collapsed ? (
            collapseToggle
          ) : (
            <>
              <div className="min-w-0 flex-1 px-1">
                <p className="font-serif text-[14px] leading-snug text-[hsl(24_14%_18%)] line-clamp-2 sm:text-[15px]">
                  {treeName || 'Family archive'}
                </p>
                {variant === 'person' && personSummary && archiveSubjectDisplayName(personSummary) ? (
                  <div className="mt-2 border-t border-[hsl(38_14%_90%)]/90 pt-2">
                    <ArchiveSubjectIdentity person={personSummary} variant="rail" />
                    {personHeldCounts && typeof onPersonViewNavigate === 'function' ? (
                      <ArchiveHeldHereLinks
                        className="mt-2 hidden text-[10px] leading-snug lg:block"
                        storyCount={personHeldCounts.storyCount ?? 0}
                        voiceMemoriesCount={personHeldCounts.voiceMemoriesCount ?? 0}
                        documentCount={personHeldCounts.documentCount ?? 0}
                        onOpenStories={() => onPersonViewNavigate('stories')}
                        onOpenVoice={() => onPersonViewNavigate('stories', { focus: 'voice' })}
                        onOpenDocuments={() => onPersonViewNavigate('documents')}
                      />
                    ) : null}
                  </div>
                ) : personLabel && variant === 'person' ? (
                  <p className="mt-1.5 text-[11px] font-normal uppercase tracking-[0.12em] text-[hsl(32_10%_48%)] line-clamp-2">
                    {personLabel}
                  </p>
                ) : null}
              </div>
              <GuideLauncher />
              {collapseToggle}
            </>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">{renderLinks(collapsed)}</div>

        {collapsed ? (
          <div className="mt-3 flex shrink-0 flex-col items-center border-t border-[hsl(38_14%_88%)]/80 pt-3">
            <GuideLauncher collapsed />
          </div>
        ) : null}

        {!collapsed && (user?.name || user?.email) && (
          <p className="mt-4 truncate px-2 text-[11px] text-muted-foreground/70" title={user.email || user.name}>
            {user.name || user.email}
          </p>
        )}
      </aside>

      {/* Mobile — unchanged */}
      <div
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)]/95 px-2 pt-2 backdrop-blur-md pb-[max(0.5rem,env(safe-area-inset-bottom,0px))] lg:hidden"
        data-testid="archive-mobile-bottom-bar"
      >
        <div className="mx-auto flex w-full max-w-md items-stretch justify-between gap-1">
          {CORE_JOURNEY_UI ? (
            <>
              <Link
                to={`${treeBase}?tab=grid`}
                className={cn(mobileBarBtn, actives.mobilePeople && 'bg-muted/70 text-foreground')}
              >
                <Users className="mx-auto h-5 w-5" />
                <span className="text-center text-[10px]">People</span>
              </Link>
              <Link
                to={`${treeBase}?tab=readiness`}
                className={cn(mobileBarBtn, actives.treeReadiness && 'bg-muted/70 text-foreground')}
              >
                <ClipboardCheck className="mx-auto h-5 w-5" />
                <span className="text-center text-[10px]">Legacy Book</span>
              </Link>
            </>
          ) : (
            <>
          <Link
            to={`${treeBase}?tab=chart`}
            className={cn(mobileBarBtn, actives.mobileTree && 'bg-muted/70 text-foreground')}
          >
            <GitBranch className="mx-auto h-5 w-5" />
            <span className="text-center text-[10px]">Tree</span>
          </Link>
          <Link
            to={`${treeBase}?tab=grid`}
            className={cn(mobileBarBtn, actives.mobilePeople && 'bg-muted/70 text-foreground')}
          >
            <Users className="mx-auto h-5 w-5" />
            <span className="text-center text-[10px]">People</span>
          </Link>
            </>
          )}
          {variant === 'person' && personId ? (
            <Link
              to={`${personHref({ id: personId })}?view=stories`}
              className={cn(mobileBarBtn, actives.mobileStories && 'bg-muted/70 text-foreground')}
            >
              <BookOpen className="mx-auto h-5 w-5" />
              <span className="text-center text-[10px]">Stories</span>
            </Link>
          ) : (
            <Link
              to="/dashboard"
              className={cn(mobileBarBtn, actives.dashboard && 'bg-muted/70 text-foreground')}
            >
              <LayoutDashboard className="mx-auto h-5 w-5" />
              <span className="text-center text-[10px]">Home</span>
            </Link>
          )}
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <button type="button" className={cn(mobileBarBtn, sheetOpen && 'bg-muted/50')}>
                <Menu className="mx-auto h-5 w-5" />
                <span className="text-center text-[10px]">Menu</span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl">
              <SheetHeader className="text-left">
                <SheetTitle className="font-serif">Family archive</SheetTitle>
                <p className="text-sm text-muted-foreground">{treeName}</p>
                {variant === 'person' && personSummary && archiveSubjectDisplayName(personSummary) ? (
                  <div className="mt-3">
                    <ArchiveSubjectIdentity person={personSummary} variant="compact" />
                    {personHeldCounts && typeof onPersonViewNavigate === 'function' ? (
                      <ArchiveHeldHereLinks
                        className="mt-2.5 text-[10px] leading-snug"
                        storyCount={personHeldCounts.storyCount ?? 0}
                        voiceMemoriesCount={personHeldCounts.voiceMemoriesCount ?? 0}
                        documentCount={personHeldCounts.documentCount ?? 0}
                        onOpenStories={() => onPersonViewNavigate('stories')}
                        onOpenVoice={() => onPersonViewNavigate('stories', { focus: 'voice' })}
                        onOpenDocuments={() => onPersonViewNavigate('documents')}
                      />
                    ) : null}
                  </div>
                ) : null}
              </SheetHeader>
              <div className="mt-4">{renderLinks(false)}</div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <ArchivePreviewModal
        open={previewOpen}
        onOpenChange={(open) => {
          if (open) markGuideExportPreviewed();
          setPreviewOpen(open);
        }}
        treeId={treeId}
        treeName={treeName}
        readinessHref={`${treeBase}?tab=readiness`}
        canExport={canExport}
        peopleCount={peopleCount}
        onExportPdf={onExportPdf}
        onExportJson={onExportJson}
        exportingPdf={exportingPdf}
        exportingJson={exportingJson}
      />
    </TooltipProvider>
  );
}
